/**
 * Prediction Utilities
 * 
 * Statistical functions for forecasting and trend analysis using simple-statistics library
 */
import * as ss from 'simple-statistics';

export interface TimeSeriesPoint {
    date: string;
    value: number;
}

export interface ForecastResult {
    date: string;
    forecast: number;
    lower: number;
    upper: number;
}

export interface DecompositionResult {
    date: string;
    value: number;
    trend: number;
    seasonal: number;
    residual: number;
}

export interface RegressionResult {
    type: string;
    equation: string;
    r2: number;
    predict: (x: number) => number;
}

/**
 * Simple Moving Average
 */
export function movingAverage(data: number[], window: number): number[] {
    if (data.length < window) return data;

    const result: number[] = [];
    for (let i = 0; i < data.length; i++) {
        if (i < window - 1) {
            result.push(data[i]);
        } else {
            const windowData = data.slice(i - window + 1, i + 1);
            result.push(ss.mean(windowData));
        }
    }
    return result;
}

/**
 * Exponential Moving Average (EMA)
 */
export function exponentialMovingAverage(data: number[], alpha: number = 0.3): number[] {
    if (data.length === 0) return [];

    const result: number[] = [data[0]];
    for (let i = 1; i < data.length; i++) {
        const ema = alpha * data[i] + (1 - alpha) * result[i - 1];
        result.push(ema);
    }
    return result;
}

/**
 * Holt-Winters Double Exponential Smoothing
 * Better for trending data
 */
export function holtWinters(
    data: number[],
    alpha: number = 0.3,
    beta: number = 0.1,
    periods: number = 7
): { smoothed: number[]; forecast: number[]; trend: number[] } {
    if (data.length < 2) {
        return { smoothed: data, forecast: [], trend: [] };
    }

    let level = data[0];
    let trend = data[1] - data[0];

    const smoothed: number[] = [level];
    const trendValues: number[] = [trend];

    for (let i = 1; i < data.length; i++) {
        const prevLevel = level;
        level = alpha * data[i] + (1 - alpha) * (level + trend);
        trend = beta * (level - prevLevel) + (1 - beta) * trend;
        smoothed.push(level);
        trendValues.push(trend);
    }

    const forecast: number[] = [];
    for (let i = 1; i <= periods; i++) {
        forecast.push(level + i * trend);
    }

    return { smoothed, forecast, trend: trendValues };
}

/**
 * Calculate confidence intervals using standard error
 */
export function confidenceIntervals(
    forecast: number[],
    historicalData: number[],
    confidenceLevel: number = 0.95
): { lower: number[]; upper: number[] } {
    const stdDev = ss.standardDeviation(historicalData);
    const zScore = confidenceLevel === 0.95 ? 1.96 : 1.28;

    const lower: number[] = [];
    const upper: number[] = [];

    for (let i = 0; i < forecast.length; i++) {
        const marginOfError = zScore * stdDev * Math.sqrt(1 + (i + 1) * 0.1);
        lower.push(Math.max(0, forecast[i] - marginOfError));
        upper.push(forecast[i] + marginOfError);
    }

    return { lower, upper };
}

/**
 * Linear Regression
 */
export function linearRegression(data: number[]): RegressionResult {
    const points: [number, number][] = data.map((y, x) => [x, y]);
    const regression = ss.linearRegression(points);
    const regressionLine = ss.linearRegressionLine(regression);
    const r2 = ss.rSquared(points, regressionLine);

    return {
        type: 'linear',
        equation: `y = ${regression.m.toFixed(2)}x + ${regression.b.toFixed(2)}`,
        r2,
        predict: regressionLine
    };
}

/**
 * Polynomial Regression (Quadratic)
 */
export function polynomialRegression(data: number[]): RegressionResult {
    const points: [number, number][] = data.map((y, x) => [x, y]);
    const n = data.length;
    const meanY = ss.mean(data);
    const lr = ss.linearRegression(points);

    // Estimate curvature from residuals
    const residuals = data.map((y, i) => y - (lr.b + lr.m * i));
    const curvature = ss.mean(residuals.map((r, i) => r * Math.pow(i - n / 2, 2))) / (n * n / 12);

    const predict = (x: number) => lr.b + lr.m * x + curvature * Math.pow(x - n / 2, 2);

    const predictions = data.map((_, i) => predict(i));
    const ssRes = ss.sum(data.map((y, i) => Math.pow(y - predictions[i], 2)));
    const ssTot = ss.sum(data.map(y => Math.pow(y - meanY, 2)));
    const r2 = ssTot > 0 ? 1 - (ssRes / ssTot) : 0;

    return {
        type: 'polynomial',
        equation: `y = ${lr.b.toFixed(2)} + ${lr.m.toFixed(2)}x + ${curvature.toFixed(4)}x²`,
        r2: Math.max(0, r2),
        predict
    };
}

/**
 * Seasonal Decomposition (Additive)
 */
export function seasonalDecomposition(
    data: TimeSeriesPoint[],
    seasonalPeriod: number = 7
): DecompositionResult[] {
    if (data.length < seasonalPeriod * 2) {
        return data.map(d => ({
            date: d.date,
            value: d.value,
            trend: d.value,
            seasonal: 0,
            residual: 0
        }));
    }

    const values = data.map(d => d.value);
    const trend = movingAverage(values, seasonalPeriod);

    const detrended = values.map((v, i) => v - trend[i]);
    const seasonalIndices: number[] = new Array(seasonalPeriod).fill(0);
    const counts: number[] = new Array(seasonalPeriod).fill(0);

    detrended.forEach((v, i) => {
        const seasonIndex = i % seasonalPeriod;
        seasonalIndices[seasonIndex] += v;
        counts[seasonIndex]++;
    });

    const avgSeasonal = seasonalIndices.map((sum, i) => sum / (counts[i] || 1));
    const seasonalMean = ss.mean(avgSeasonal);
    const normalizedSeasonal = avgSeasonal.map(s => s - seasonalMean);

    return data.map((d, i) => ({
        date: d.date,
        value: d.value,
        trend: trend[i],
        seasonal: normalizedSeasonal[i % seasonalPeriod],
        residual: d.value - trend[i] - normalizedSeasonal[i % seasonalPeriod]
    }));
}

/**
 * Monte Carlo Simulation for Cash Flow
 */
export function monteCarloSimulation(
    historicalValues: number[],
    periods: number = 30,
    simulations: number = 1000
): { percentile5: number[]; percentile50: number[]; percentile95: number[] } {
    if (historicalValues.length < 7) {
        return { percentile5: [], percentile50: [], percentile95: [] };
    }

    const changes: number[] = [];
    for (let i = 1; i < historicalValues.length; i++) {
        changes.push(historicalValues[i] - historicalValues[i - 1]);
    }

    const meanChange = ss.mean(changes);
    const stdChange = ss.standardDeviation(changes);

    const allSimulations: number[][] = [];
    const lastValue = historicalValues[historicalValues.length - 1];

    for (let sim = 0; sim < simulations; sim++) {
        const path: number[] = [lastValue];
        let current = lastValue;

        for (let t = 0; t < periods; t++) {
            // Box-Muller transform for normal distribution
            const u1 = Math.random();
            const u2 = Math.random();
            const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
            const randomChange = z * stdChange + meanChange;
            current = Math.max(0, current + randomChange);
            path.push(current);
        }

        allSimulations.push(path);
    }

    const percentile5: number[] = [];
    const percentile50: number[] = [];
    const percentile95: number[] = [];

    for (let t = 0; t <= periods; t++) {
        const valuesAtT = allSimulations.map(sim => sim[t]).sort((a, b) => a - b);
        percentile5.push(ss.quantile(valuesAtT, 0.05));
        percentile50.push(ss.quantile(valuesAtT, 0.50));
        percentile95.push(ss.quantile(valuesAtT, 0.95));
    }

    return { percentile5, percentile50, percentile95 };
}

/**
 * Anomaly Detection using Z-Score
 */
export function detectAnomalies(
    data: number[],
    threshold: number = 2.5
): { index: number; value: number; zScore: number }[] {
    const mean = ss.mean(data);
    const stdDev = ss.standardDeviation(data);

    const anomalies: { index: number; value: number; zScore: number }[] = [];

    data.forEach((value, index) => {
        const zScore = stdDev > 0 ? (value - mean) / stdDev : 0;
        if (Math.abs(zScore) > threshold) {
            anomalies.push({ index, value, zScore });
        }
    });

    return anomalies;
}

/**
 * Calculate seasonal indices
 */
export function calculateSeasonalIndices(
    data: TimeSeriesPoint[],
    period: 'dayOfWeek' | 'month' = 'dayOfWeek'
): { index: number; label: string; avgValue: number; seasonalIndex: number }[] {
    const buckets: Record<number, number[]> = {};

    data.forEach(point => {
        const date = new Date(point.date);
        const key = period === 'dayOfWeek' ? date.getDay() : date.getMonth();

        if (!buckets[key]) buckets[key] = [];
        buckets[key].push(point.value);
    });

    const overallMean = ss.mean(data.map(d => d.value));

    const labels = period === 'dayOfWeek'
        ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return Object.entries(buckets)
        .map(([key, values]) => {
            const idx = parseInt(key);
            const avgValue = ss.mean(values);
            return {
                index: idx,
                label: labels[idx],
                avgValue,
                seasonalIndex: overallMean > 0 ? avgValue / overallMean : 1
            };
        })
        .sort((a, b) => a.index - b.index);
}

/**
 * Calculate trend strength (0-1)
 */
export function trendStrength(data: number[]): number {
    if (data.length < 3) return 0;
    const lr = linearRegression(data);
    return Math.sqrt(Math.max(0, lr.r2));
}

/**
 * Calculate growth rate (CAGR-style)
 */
export function growthRate(startValue: number, endValue: number, periods: number): number {
    if (startValue <= 0 || periods <= 0) return 0;
    return Math.pow(endValue / startValue, 1 / periods) - 1;
}

/**
 * Platform-specific ROAS forecasting models using diminishing returns (Hill function)
 * and time-decay (ad fatigue) curves.
 */

const PLATFORM_PARAMS: Record<string, { halfSatSpend: number; fatigueHalfLife: number; maxSatDrop: number }> = {
    'meta': { halfSatSpend: 500, fatigueHalfLife: 45, maxSatDrop: 0.5 },
    'tiktok': { halfSatSpend: 300, fatigueHalfLife: 25, maxSatDrop: 0.55 },
    'google': { halfSatSpend: 1000, fatigueHalfLife: 90, maxSatDrop: 0.4 },
    'default': { halfSatSpend: 500, fatigueHalfLife: 45, maxSatDrop: 0.5 }
};

/** Get the default hardcoded half-life for a given platform */
export function getDefaultHalfLife(platform: string): number {
    return (PLATFORM_PARAMS[platform.toLowerCase()] || PLATFORM_PARAMS['default']).fatigueHalfLife;
}

/**
 * Fit a fatigue half-life from actual ROAS time-series using smoothed trend.
 * Returns the fitted half-life in days, or null if data is insufficient.
 */
export function fitFatigueHalfLife(validRoasValues: number[], minDataPoints: number = 14): number | null {
    if (validRoasValues.length < minDataPoints) return null;

    const alpha = 0.2, beta = 0.08;
    let level = validRoasValues[0], trend = 0;

    if (validRoasValues.length > 3) {
        const diffs: number[] = [];
        for (let i = 1; i < Math.min(validRoasValues.length, 5); i++) {
            diffs.push(validRoasValues[i] - validRoasValues[i - 1]);
        }
        trend = diffs.reduce((a, b) => a + b, 0) / diffs.length;
    }

    for (let i = 1; i < validRoasValues.length; i++) {
        const prev = level;
        level = alpha * validRoasValues[i] + (1 - alpha) * (level + trend);
        trend = beta * (level - prev) + (1 - beta) * trend;
    }

    // If trend is non-negative, ROAS is flat/rising → very slow decay
    if (trend >= 0 || level <= 0) return 180;

    const dailyDecayFraction = Math.abs(trend) / level;
    const halfLife = Math.LN2 / dailyDecayFraction;
    return Math.max(7, Math.min(180, halfLife));
}

/**
 * Spend saturation multiplier (Hill function).
 * Returns a 0..1 multiplier. At spend=0 → 1.0, at spend=∞ → (1 - maxSatDrop).
 */
export function calculateSpendSaturationRoas(spend: number, platform: string = 'default'): number {
    const params = PLATFORM_PARAMS[platform.toLowerCase()] || PLATFORM_PARAMS['default'];
    if (spend <= 0) return 1.0;

    const steepness = 1.3;
    const saturation = Math.pow(spend, steepness) / (Math.pow(spend, steepness) + Math.pow(params.halfSatSpend, steepness));
    return Math.max(0.15, 1 - saturation * params.maxSatDrop);
}

/**
 * Incremental fatigue modifier with optional custom half-life override.
 * @param additionalDays Days into the future (0 = today)
 * @param platform Platform (for default half-life)
 * @param customHalfLife Optional data-driven half-life override
 */
export function calculateAdFatigueModifier(
    additionalDays: number,
    platform: string = 'default',
    customHalfLife?: number
): number {
    const halfLife = customHalfLife ?? (PLATFORM_PARAMS[platform.toLowerCase()] || PLATFORM_PARAMS['default']).fatigueHalfLife;
    if (additionalDays <= 0) return 1.0;
    return Math.max(0.3, Math.pow(0.5, additionalDays / halfLife));
}

/**
 * Combined predicted ROAS for a single forecast day.
 * 
 * @param spend Predicted daily spend for this day
 * @param recentBaselineRoas The ROAS observed in the recent window (not all-time)
 * @param forecastDayIndex How many days into the future (0-based: 0=tomorrow)
 * @param platform Platform identifier
 */
export function predictEntityRoas(
    spend: number,
    recentBaselineRoas: number,
    forecastDayIndex: number,
    platform: string = 'default',
    customHalfLife?: number
): number {
    const saturationMult = calculateSpendSaturationRoas(spend, platform);
    const fatigueMult = calculateAdFatigueModifier(forecastDayIndex, platform, customHalfLife);
    return recentBaselineRoas * saturationMult * fatigueMult;
}
